import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, EarIcon, MessageSquareIcon, MicIcon, RefreshCwIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ReviewsGallery } from '../components/ReviewsGallery';
import { EnrollmentOffer } from '../components/EnrollmentOffer';
import { FAQSection } from '../components/FAQSection';
export function Course4Page() {
  const modules = [{
    id: '01',
    title: 'Limbaj receptiv vs. Expresiv',
    icon: <EarIcon size={48} className="text-brand-gold" data-id="element-761" />,
    bullets: ['De ce copilul înțelege tot dar nu vorbește', 'Cum să verifici corect cât înțelege copilul tău', 'Primii pași pentru a debloca limbajul expresiv']
  }, {
    id: '02',
    title: 'Comunicarea funcțională',
    icon: <MessageSquareIcon size={48} className="text-brand-gold" data-id="element-762" />,
    bullets: ['Cum să-l înveți să ceară ce vrea fără să plângă sau să te tragă de mână', 'Rolul gesturilor (arătatul cu degetul) și cum să le stimulezi', 'De ce "spune X" nu funcționează și ce să faci în loc']
  }, {
    id: '03',
    title: 'De la sunete la cuvinte',
    icon: <MicIcon size={48} className="text-brand-gold" data-id="element-763" />,
    bullets: ['Etapele apariției limbajului: de la gângurit la primele cuvinte', 'Cum să folosești onomatopeele corect', 'Tehnici specifice pentru a încuraja imitarea verbală']
  }, {
    id: '04',
    title: 'Stimularea limbajului în rutine',
    icon: <RefreshCwIcon size={48} className="text-brand-gold" data-id="element-764" />,
    bullets: ['Cum să vorbești cu copilul tău astfel încât să te asculte și să învețe', 'Greșeli frecvente pe care le fac părinții când încearcă să-și facă copilul să vorbească', 'Cum să folosești momentele de baie, masă și îmbrăcat pentru a dezvolta limbajul']
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="pt-[64px] md:pt-[72px]" data-id="element-765">
      {/* SECȚIUNEA 1 — HERO OFERTĂ */}
      <section className="bg-brand-navy section-padding text-white" data-id="element-766">
        <div className="container-custom" data-id="element-767">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-768">
            <div className="max-w-xl" data-id="element-769">
              <h1 className="font-playfair text-[48px] md:text-[64px] font-bold text-white mb-6 leading-[1.1]" data-id="element-770">
                Structurarea Comunicării
              </h1>
              <p className="font-source text-[20px] md:text-[24px] text-brand-gold mb-10 italic" data-id="element-771">
                Învață cum să-l ajuți să comunice, de la primele sunete la
                propoziții, chiar dacă acum pare că nu vrea sau nu poate.
              </p>
              <a href="#inscriere" className="btn-inverted text-[18px] px-8 py-4" data-id="element-772">
                Click aici pentru înscriere!
              </a>
            </div>
            <div className="relative rounded-[24px] overflow-hidden shadow-2xl border-4 border-white/10" data-id="element-773">
              <img src="https://assets.cdn.filesafe.space/3SQqBp51ZHIpTGzJ3Vu1/media/6a2bf33308984b712926c140.jpg" alt="Structurarea Comunicării" className="w-full h-full object-cover aspect-[4/3]" data-id="element-774" />
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 2 — DESCRIERE */}
      <section className="bg-brand-beige section-padding" data-id="element-775">
        <div className="container-custom" data-id="element-776">
          <div className="max-w-3xl mx-auto" data-id="element-777">
            <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy mb-8 text-center" data-id="element-778">
              Copilul tău înțelege tot, dar nu vorbește?
            </h2>
            <div className="font-source text-[16px] md:text-[18px] text-brand-text space-y-6" data-id="element-779">
              <p data-id="element-780">
                Este frustrant să vezi că puiul tău știe ce vrea, dar nu poate
                să-ți spună. Te trage de mână, arată cu degetul sau, mai rău,
                plânge și se enervează pentru că nu reușești să ghicești din
                prima ce își dorește. Ai încercat să-l pui să repete cuvinte,
                i-ai spus "spune mama", "spune apă", dar pare că se blochează și
                mai tare sau pur și simplu te ignoră.
              </p>
              <p data-id="element-781">
                Această lipsă de comunicare nu doar că îl frustrează pe el, dar
                te epuizează și pe tine. Fiecare zi devine un joc de ghicit, iar
                teama că "ceva nu e în regulă" crește cu fiecare lună care trece
                fără ca el să vorbească.
              </p>
              <div className="bg-white p-6 rounded-[16px] border-l-4 border-brand-coral" data-id="element-782">
                <p className="font-medium text-brand-navy" data-id="element-783">
                  Cursul "S - Structurarea Comunicării" îți arată exact cum să
                  deblochezi limbajul copilului tău. Vei învăța tehnici blânde,
                  testate în cabinet, pe care le poți aplica direct în rutinele
                  voastre zilnice, fără să-l presezi și fără să transformi casa
                  într-o sală de terapie.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 3 — CE VEI DESCOPERI */}
      <section className="bg-brand-ivory section-padding" data-id="element-784">
        <div className="container-custom" data-id="element-785">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-16" data-id="element-786">
            Ce vei descoperi concret în Cursul 4: Structurarea Comunicării
          </h2>
          <div className="max-w-4xl mx-auto space-y-8" data-id="element-787">
            {modules.map(mod => <div key={mod.id} className="flex flex-col md:flex-row bg-white rounded-[24px] shadow-sm border border-gray-100 overflow-hidden p-8 gap-8 items-start" data-id="element-788">
                {/* Icon Side */}
                <div className="flex-shrink-0 w-24 h-24 rounded-full bg-brand-beige flex items-center justify-center" data-id="element-789">
                  {mod.icon}
                </div>

                {/* Content Side */}
                <div className="flex-grow" data-id="element-790">
                  <h3 className="font-playfair text-[24px] md:text-[28px] font-bold text-brand-navy mb-6 leading-[1.2]" data-id="element-791">
                    {mod.title}
                  </h3>
                  <ul className="space-y-4 flex flex-col" data-id="element-792">
                    {mod.bullets.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-793">
                        <CheckCircle2Icon className="text-brand-gold flex-shrink-0 mt-1" size={20} data-id="element-794" />
                        <span className="font-source text-[15px] text-brand-text" data-id="element-795">
                          {item}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 4 — BENEFICII */}
      <section className="bg-brand-beige section-padding" data-id="element-796">
        <div className="container-custom" data-id="element-797">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-12" data-id="element-798">
            Ce se schimbă după ce parcurgi acest curs
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-799">
            {[{
            title: 'Știi exact de ce nu vorbește copilul tău',
            desc: 'Nu mai ghicești. Înțelegi diferența dintre limbajul receptiv și cel expresiv și știi exact unde este blocajul.'
          }, {
            title: 'Ai tehnici clare de stimulare',
            desc: 'Nu mai folosești metode care frustrează copilul (cum ar fi "spune mama"). Ai la dispoziție tehnici blânde, bazate pe joc și rutine, care funcționează.'
          }, {
            title: 'Scapi de frustrarea comunicării',
            desc: 'Când copilul învață să ceară funcțional, crizele de plâns și frustrarea scad dramatic, atât pentru el, cât și pentru tine.'
          }, {
            title: 'Transformi orice moment în oportunitate de învățare',
            desc: 'Nu ai nevoie de ore speciale de "terapie" acasă. Înveți cum să folosești baia, masa și plimbarea pentru a stimula limbajul natural.'
          }].map((card, i) => <div key={i} className="bg-white p-6 rounded-[16px] shadow-sm border-t-4 border-brand-gold" data-id="element-800">
                <h3 className="font-montserrat font-bold text-[16px] text-brand-navy mb-3" data-id="element-801">
                  {card.title}
                </h3>
                <p className="font-source text-[14px] text-brand-text" data-id="element-802">
                  {card.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 5 — CINE SUNT EU */}
      <section className="bg-brand-ivory section-padding" data-id="element-803">
        <div className="container-custom" data-id="element-804">
          <div className="max-w-4xl mx-auto" data-id="element-805">
            <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-10" data-id="element-806">
              Cine sunt eu și de ce am creat acest curs
            </h2>
            <div className="flex flex-col-reverse md:grid md:grid-cols-3 gap-8 items-stretch" data-id="element-807">
              <div className="md:col-span-2 font-source text-[16px] md:text-[18px] text-brand-text space-y-4 text-center md:text-left flex flex-col justify-center" data-id="element-808">
                <p data-id="element-809">
                  Sunt Cristina Geană, psiholog clinician cu peste 11 ani de
                  experiență directă cu copii mici. Am lucrat cu sute de copii
                  cu întârzieri de limbaj și am văzut cât de mult se schimbă
                  dinamica familiei când copilul reușește, în sfârșit, să
                  comunice ce își dorește.
                </p>
                <p data-id="element-810">
                  Am creat acest curs pentru a-ți oferi tehnicile pe care le
                  folosesc eu în cabinet, adaptate pentru a fi folosite ușor,
                  acasă, de către orice mamă.
                </p>
              </div>
              <div className="md:col-span-1" data-id="element-811">
                <img src="https://cdn.magicpatterns.com/uploads/wvUBHAy6VhqaRqNiVJPB6X/Cristina_poze_%285%29.jpg" alt="Cristina Geană" className="w-full h-full object-cover object-top rounded-[24px] shadow-card" data-id="element-812" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 6 — PENTRU CINE ESTE / NU ESTE */}
      <section className="bg-brand-navy section-padding text-white" data-id="element-813">
        <div className="container-custom" data-id="element-814">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-white text-center mb-12" data-id="element-815">
            Pentru cine este acest curs?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-816">
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-[24px] border border-white/20" data-id="element-817">
              <h3 className="font-playfair text-[28px] font-bold text-brand-gold mb-6 !text-brand-gold" data-id="element-818">
                Pentru cine este
              </h3>
              <ul className="space-y-4" data-id="element-819">
                {['Mame de copii care înțeleg tot dar nu vorbesc', 'Mame de copii care comunică doar trăgând de mână sau plângând', 'Mame care simt că metodele clasice ("spune X") nu dau rezultate'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-820">
                    <CheckCircle2Icon className="text-brand-gold flex-shrink-0 mt-0.5" size={20} data-id="element-821" />
                    <span className="font-source text-[15px] text-white/90" data-id="element-822">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
            <div className="bg-white/5 p-8 rounded-[24px] border border-white/10" data-id="element-823">
              <h3 className="font-playfair text-[28px] font-bold text-white mb-6 !text-white" data-id="element-824">
                Pentru cine NU este
              </h3>
              <ul className="space-y-4" data-id="element-825">
                {['Mame care caută o rețetă magică și rapidă fără să fie dispuse să aplice tehnicile zilnic', 'Mame care cred că limbajul se va dezvolta de la sine, fără nicio intervenție', 'Profesioniști care caută un curs de specialitate. Acest curs e pentru mame, în limbaj accesibil.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-826">
                    <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-827">
                      <span className="w-1.5 h-1.5 rounded-full bg-white/60" data-id="element-828"></span>
                    </div>
                    <span className="font-source text-[15px] text-white/70" data-id="element-829">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 7 — OFERTĂ */}
      <EnrollmentOffer title="Ce primești și care este investiția ta" includes={['Acces pe viață la Modulul 4 - Structurarea Comunicării', 'Materiale video structurate pas cu pas', 'Tehnici practice de aplicat acasă', 'Informație clară, fără termeni medicali']} price="395 lei" note="Acest modul face parte din Colecția Completă Uite cum C.R.E.S.C., dar poate fi achiziționat și separat." data-id="element-830" />

      {/* SECȚIUNEA 8 — TESTIMONIALE */}
      <section className="bg-brand-ivory section-padding" data-id="element-831">
        <div className="container-custom" data-id="element-832">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-12" data-id="element-833">
            Ce spun mamele care deja au lucrat cu mine
          </h2>
          <ReviewsGallery data-id="element-834" />
        </div>
      </section>

      {/* SECȚIUNEA 9 — FAQ */}
      <FAQSection faqs={[{
      q: 'Când primesc acces la curs?',
      a: 'Imediat după confirmarea plății, vei primi pe email datele de acces la platforma unde se află cursul.'
    }, {
      q: 'Cât timp am acces la materiale?',
      a: 'Ai acces pe viață la acest modul. Îl poți revedea ori de câte ori ai nevoie, în ritmul tău.'
    }, {
      q: 'Pot cumpăra și restul modulelor mai târziu?',
      a: 'Da, poți achiziționa celelalte module separat sau poți opta pentru Colecția Completă C.R.E.S.C. pentru a beneficia de prețul special și de apelurile de grup.'
    }]} data-id="element-835" />

      {/* SECȚIUNEA 10 — ÎNSCRIERE */}
      <section id="inscriere" className="bg-brand-navy section-padding text-center text-white" data-id="element-836">
        <div className="container-custom" data-id="element-837">
          <div className="max-w-3xl mx-auto" data-id="element-838">
            <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-white mb-6" data-id="element-839">
              Înscriere Modulul 4 - Structurarea Comunicării
            </h2>
            <p className="font-source text-[18px] text-white/80 mb-8" data-id="element-840">
              Completează formularul de mai jos pentru a finaliza înscrierea.
            </p>

            <div className="bg-white rounded-[24px] overflow-hidden min-h-[600px]" data-id="element-841">
              <iframe src="https://api.leadconnectorhq.com/widget/form/VG54ypo3Iqugibc7IBOc" style={{
              width: '100%',
              height: '100%',
              minHeight: '600px',
              border: 'none'
            }} id="inline-VG54ypo3Iqugibc7IBOc" data-layout="{'id':'INLINE'}" data-trigger-type="alwaysShow" data-trigger-value="" data-activation-type="alwaysActivated" data-activation-value="" data-deactivation-type="neverDeactivate" data-deactivation-value="" data-form-name="Form Modul 4" data-height="600" data-layout-iframe-id="inline-VG54ypo3Iqugibc7IBOc" data-form-id="VG54ypo3Iqugibc7IBOc" title="Form Modul 4" data-id="element-842" />
            </div>
          </div>
        </div>
      </section>
    </motion.div>;
}